import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { ISection } from "../../../types/section";

import useReorderSection from "../../../hooks/section/useReorderSection";
import useReorderPage from "../../../hooks/page/useReorderPage";
import useReorderQuestion from "../../../hooks/question/useReorderQuestion";

import { Icons } from '../../../data/Assets';

import { SVGIcon } from '../../wrappers/SVGIcon';

const SideBar = ({
  sections,
  setSections,
  currOption,
  setCurrOption,
}: {
  sections: ISection[];
  setSections: React.Dispatch<React.SetStateAction<ISection[]>>;
  currOption: number[];
  setCurrOption: React.Dispatch<React.SetStateAction<number[]>>;
}) => {

  const params = useParams();

  const [showNewSection, setShowNewSection] = useState<boolean>(false);
  const [treeStack, setTreeStack] = useState<
    { sec: number; pg: number; ques: number }[]
  >([]);
  const [showSection, setShowSection] = useState<Set<number>>(new Set());
  const [showPage, setShowPage] = useState<{ sec: number; pg: number }[]>([]);
  const [showNewPage, setShowNewPage] = useState<boolean>(false);
  const [showNewQuestion, setShowNewQuestion] = useState<boolean>(false);

  const { mutate: reorderSection } = useReorderSection();
  const { mutate: reorderPage } = useReorderPage();
  const { mutate: reorderQuestion } = useReorderQuestion();

  const isSelect = (checkArr: number[]) => {
    if (checkArr.length !== currOption.length) return "";

    for (let i = 0; i < checkArr.length; i++) {
      if (checkArr[i] !== currOption[i]) return "";
    }
    return "selected";
  };

  const expandorContractSection = (i: number) => {
    setCurrOption([i]);

    const hasSection = treeStack.filter((stack) => stack.sec === i).length;
    if (hasSection) {
      setTreeStack((prev) => prev.filter((stack) => stack.sec !== i));
      const delSection = showSection;
      delSection.delete(i);
      setShowSection(delSection);
    } else {
      const oldSection = [...showSection, i];
      setShowSection(new Set([...oldSection]));
      setTreeStack([...treeStack, { sec: i, pg: -1, ques: -1 }]);
    }
  };

  const expandorContractPage = (i: number, j: number) => {
    setCurrOption([i, j]);

    const hasPage = treeStack.filter(
      (stack) => stack.sec === i && stack.pg === j
    ).length;
    if (hasPage) {
      setTreeStack((prev) =>
        prev.filter((stack) => !(stack.sec === i && stack.pg === j))
      );
      setShowPage((prev) =>
        prev.filter((page) => !(page.sec === i && page.pg === j))
      );
    } else {
      setShowPage([...showPage, { sec: i, pg: j }]);
      setTreeStack([...treeStack, { sec: i, pg: j, ques: -1 }]);
    }
  };

  const isPageExpand = (sec: number, pg: number) => {
    const expandPage =
      showPage.filter((page) => page.sec === sec && page.pg === pg).length !==
      0;
    return expandPage;
  };

  const resetStack = () => {
    setCurrOption([sections.length]);
    setTreeStack([]);
    setShowSection(new Set());
    setShowPage([]);
  };

  useEffect(() => {
    if (params.questcode) {
      setTreeStack([]);
      setShowSection(new Set());
      setShowPage([]);
    }
  }, [params.questcode]);

  useEffect(() => {
    if (currOption[0] === sections.length) {
      setShowNewSection(true);
    } else {
      setShowNewSection(false);
    }

    if (currOption[1] === sections[currOption[0]]?.pages.length) {
      setShowNewPage(true);
    } else {
      setShowNewPage(false);
    }

    if (
      currOption.length > 2 &&
      currOption[2] ===
      sections[currOption[0]]?.pages[currOption[1]]?.questions.length
    ) {
      setShowNewQuestion(true);
    } else {
      setShowNewQuestion(false);
    }
  }, [currOption[0], currOption[1], currOption[2], sections]); //eslint-disable-line

  useEffect(() => {
    setShowNewSection(false);
  }, [sections.length]); //eslint-disable-line

  const pagLen = sections[currOption[0]]?.pages.length || 0;
  useEffect(() => {
    setShowNewPage(false);
  }, [pagLen]);

  const questLen =
    sections[currOption[0]]?.pages[currOption[1]]?.questions.length || 0;
  useEffect(() => {
    setShowNewQuestion(false);
  }, [questLen]);

  const handleCreateSectionNav = () => {
    resetStack();
    setShowNewSection(true);
  };

  const handleCreatePageNav = (secId: number) => {
    setCurrOption([secId, sections[secId].pages.length]);
    setShowNewPage(true);
  };

  const handleCreateQuesNav = (secId: number, pageId: number) => {
    setCurrOption([
      secId,
      pageId,
      sections[secId].pages[pageId].questions.length,
    ]);
    setShowNewQuestion(true);
  };

  const swapPosSection = (x: number, y: number) => {
    reorderSection({ id: sections[x].id ?? 0, action: x === y - 1 ? "down" : "up" });
    const newArr = sections;
    const temp = newArr[x];
    newArr[x] = newArr[y];
    newArr[y] = temp;
    setCurrOption([y]);
    setSections([...newArr]);
  };

  const moveSectionUp = () => {
    if (currOption[0] === 0) {
      // do not move up
      console.log("Cannot move up!");
    } else {
      resetStack();
      swapPosSection(currOption[0], currOption[0] - 1);
    }
  };

  const moveSectionDown = () => {
    if (currOption[0] === sections.length - 1) {
      // do not move down
      console.log("Cannot move down!");
    } else {
      resetStack();
      swapPosSection(currOption[0], currOption[0] + 1);
    }
  };

  const swapPosPage = (x: number, y: number) => {
    reorderPage({
      id: sections[currOption[0]].pages[x].id ?? 0,
      action: x === y - 1 ? "down" : "up",
    });
    const newArr = sections;
    const temp = sections[currOption[0]].pages[x];
    newArr[currOption[0]].pages[x] = newArr[currOption[0]].pages[y];
    newArr[currOption[0]].pages[y] = temp;

    setSections([...newArr]);
    setCurrOption([currOption[0], y]);
  };

  const movePageUp = () => {
    if (currOption[1] === 0) {
      // do not move up
      console.log("Cannot move up!");
    } else {
      setShowPage([]);
      swapPosPage(currOption[1], currOption[1] - 1);
    }
  };

  const movePageDown = () => {
    if (currOption[1] === sections[currOption[0]].pages.length - 1) {
      // do not move down
      console.log("Cannot move down!");
    } else {
      setShowPage([]);
      swapPosPage(currOption[1], currOption[1] + 1);
    }
  };

  const swapPosQuestion = (x: number, y: number) => {
    reorderQuestion({
      id: sections[currOption[0]].pages[currOption[1]].questions[x].id ?? 0,
      action: x === y - 1 ? "down" : "up",
    });
    const newArr = sections;
    const temp = sections[currOption[0]].pages[currOption[1]].questions[x];
    newArr[currOption[0]].pages[currOption[1]].questions[x] =
      newArr[currOption[0]].pages[currOption[1]].questions[y];
    newArr[currOption[0]].pages[currOption[1]].questions[y] = temp;

    setSections([...newArr]);
    setCurrOption([currOption[0], currOption[1], y]);
  };

  const moveQuestionUp = () => {
    if (currOption[2] === 0) {
      // do not move up
      console.log("Cannot move up!");
    } else {
      swapPosQuestion(currOption[2], currOption[2] - 1);
    }
  };

  const moveQuestionDown = () => {
    if (
      currOption[2] ===
      sections[currOption[0]].pages[currOption[1]].questions.length - 1
    ) {
      // do not move down
      console.log("Cannot move down!");
    } else {
      swapPosQuestion(currOption[2], currOption[2] + 1);
    }
  };

  const movingUpButtonsDisableCondition = () => {
    if (currOption.length === 1) {
      return currOption[0] === sections.length || currOption[0] === 0;
    } else if (currOption.length === 2) {
      return (
        currOption[1] === sections[currOption[0]].pages.length ||
        currOption[1] === 0
      );
    } else if (currOption.length === 3) {
      return (
        currOption[2] ===
        sections[currOption[0]].pages[currOption[1]].questions.length ||
        currOption[2] === 0
      );
    }
  };

  const movingDownButtonsDisableCondition = () => {
    if (currOption.length === 1) {
      return currOption[0] >= sections.length - 1;
    } else if (currOption.length === 2) {
      return currOption[1] >= sections[currOption[0]].pages.length - 1;
    } else if (currOption.length === 3) {
      return (
        currOption[2] >=
        sections[currOption[0]].pages[currOption[1]].questions.length - 1
      );
    }
  };

  return (
    <div className="side_bar">
      <div className="options">
        <button
          disabled={showNewSection || showNewPage || showNewQuestion}
          onClick={handleCreateSectionNav}
        >
          <SVGIcon src={Icons.Plus} />
          Section
        </button>
        <button
          onClick={() => handleCreatePageNav(currOption[0])}
          disabled={
            showNewSection ||
            showNewPage ||
            showNewQuestion ||
            currOption.length < 1 ||
            sections.length === 0
            // !showSection.has(currOption[0])
          }
        >
          <SVGIcon src={Icons.Plus} />
          Page
        </button>
        <button
          onClick={() => handleCreateQuesNav(currOption[0], currOption[1])}
          disabled={
            showNewSection ||
            showNewPage ||
            showNewQuestion ||
            currOption.length < 2
            // !isPageExpand(currOption[0], currOption[1])
          }
        >
          <SVGIcon src={Icons.Plus} />
          Question
        </button>
      </div>

      <div className="options_tree">
        {(showNewSection || sections.length === 0) && (
          <div
            id="new_section"
            className={`section cont selected`}
            onClick={() => setCurrOption([sections.length])}
          >
            <SVGIcon src={Icons.PointFill} /> <p>New section</p>
          </div>
        )}
        {sections.map((section, i) => (
          <div key={i}>
            <div
              className={`section cont ${isSelect([i])} ${showSection.has(i) ? "rotate_svg" : ""
                }`}
              onClick={() => expandorContractSection(i)}
            >
              <SVGIcon src={Icons.TriangleFill} /> <p>{section.title}</p>
            </div>
            {showNewPage && currOption[0] === i && (
              <div
                id="new_page"
                className={`page cont selected`}
                onClick={() => setCurrOption([i, sections[i].pages.length])}
              >
                <SVGIcon src={Icons.PointFill} /> <p>New Page</p>
              </div>
            )}
            {(showSection.has(i) || (showNewPage && currOption[0] === i)) &&
              section.pages.map((page, j) => (
                <div key={j}>
                  <div
                    className={`page cont ${isSelect([i, j])} ${isPageExpand(i, j) ? "rotate_svg" : ""
                      }`}
                    onClick={() => expandorContractPage(i, j)}
                  >
                    <SVGIcon src={Icons.TriangleFill} /> <p>{page.title}</p>
                  </div>
                  {showNewQuestion &&
                    currOption[0] === i &&
                    currOption[1] === j && (
                      <div
                        className={`question cont selected`}
                        onClick={() =>
                          setCurrOption([
                            i,
                            j,
                            sections[1].pages[j].questions.length,
                          ])
                        }
                      >
                        <p>New Question</p>
                      </div>
                    )}
                  {(isPageExpand(i, j) || (showNewQuestion && currOption[0] === i && currOption[1] === j)) &&
                    page.questions.map(({ question }, k) => (
                      <div
                        key={k}
                        className={`question cont ${isSelect([i, j, k])}`}
                        onClick={() => setCurrOption([i, j, k])}
                      >
                        <p>{question}</p>
                      </div>
                    ))}
                </div>
              ))}
          </div>
        ))}
      </div>

      <div className="options">
        <button
          onClick={
            currOption.length === 1
              ? moveSectionUp
              : currOption.length === 2
                ? movePageUp
                : moveQuestionUp
          }
          disabled={
            movingUpButtonsDisableCondition() ||
            showNewQuestion ||
            showNewPage ||
            showNewSection
          }
        >
          <SVGIcon src={Icons.ArrowShort} rotation={180} />
          Move Up
        </button>
        <button
          onClick={
            currOption.length === 1
              ? moveSectionDown
              : currOption.length === 2
                ? movePageDown
                : moveQuestionDown
          }
          disabled={
            movingDownButtonsDisableCondition() ||
            showNewQuestion ||
            showNewPage ||
            showNewSection
          }
        >
          <SVGIcon src={Icons.ArrowShort} />
          Move Down
        </button>
      </div>
    </div>
  );
};

export default SideBar;
